import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import HistoryIcon from "@mui/icons-material/History";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarIcon from "@mui/icons-material/Star";
import UpdateIcon from "@mui/icons-material/Update";
import toast from "react-hot-toast";
import {
  createReportDefinition,
  deleteReportDefinition,
  getReportDefinition,
  listReportDefinitions,
  pinReportDefinition,
  subscribeReportDefinitionsInvalidation,
  updateReportDefinition,
} from "../../../../services/reportDefinitions";
import { DEFAULT_REPORT_KEY, normalizeReportKey } from "./reportKeys";

const makeConfig = ({ instanceUniqueId, reportKey, ui, pinned = false }) => ({
  version: 2,
  reportKey,
  instanceUniqueId,
  pinned: Boolean(pinned),
  ui,
});

const readConfig = (definition) => {
  const config = definition?.config_json || definition?.config || {};
  return config?.ui || definition?.ui || null;
};

const isPinned = (definition) =>
  Boolean(
    definition?.config_json?.pinned
    ?? definition?.config?.pinned
    ?? definition?.config_json?.ui?.pinned
    ?? definition?.config?.ui?.pinned
    ?? false,
  );

const canEditDefinition = (definition) => Boolean(definition?.canEdit);

export default function ReportSavedReportsButton({
  instanceUniqueId,
  reportKey,
  currentState,
  onApplyReport,
  title = "Saved reports",
}) {
  const normalizedReportKey = normalizeReportKey(reportKey, DEFAULT_REPORT_KEY) || DEFAULT_REPORT_KEY;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [definitions, setDefinitions] = useState([]);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [selectedDefinition, setSelectedDefinition] = useState(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [applying, setApplying] = useState(false);
  const [deletingIds, setDeletingIds] = useState({});
  const [pinningIds, setPinningIds] = useState({});

  const [createTitle, setCreateTitle] = useState("");
  const [createDesc, setCreateDesc] = useState("");
  const [createVisibility, setCreateVisibility] = useState("private");

  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editVisibility, setEditVisibility] = useState("private");

  const listRequestSeq = useRef(0);
  const selectedRequestSeq = useRef(0);

  const refreshDefinitions = useCallback(async () => {
    if (!open) return;
    const requestId = ++listRequestSeq.current;
    try {
      setLoading(true);
      const data = await listReportDefinitions(instanceUniqueId, {
        reportKey: normalizedReportKey,
        q: query || undefined,
        limit: 200,
      });
      if (requestId !== listRequestSeq.current) return;
      setDefinitions(Array.isArray(data) ? data : []);
    } catch (error) {
      if (requestId !== listRequestSeq.current) return;
      console.error(error);
      toast.error(error?.response?.data?.message || "Could not load saved reports");
    } finally {
      if (requestId === listRequestSeq.current) setLoading(false);
    }
  }, [instanceUniqueId, normalizedReportKey, open, query]);

  const loadSelectedDefinition = useCallback(async (definitionId) => {
    if (!definitionId) {
      setSelectedDefinition(null);
      return;
    }
    const requestId = ++selectedRequestSeq.current;
    try {
      const definition = await getReportDefinition(definitionId, instanceUniqueId, {
        reportKey: normalizedReportKey,
      });
      if (requestId !== selectedRequestSeq.current) return;
      setSelectedDefinition(definition || null);
      setEditTitle(definition?.name || "");
      setEditDesc(definition?.description || "");
      setEditVisibility("private");
    } catch (error) {
      if (requestId !== selectedRequestSeq.current) return;
      console.error(error);
      if (error?.response?.status === 404) {
        setSelectedId(null);
        setSelectedDefinition(null);
        await refreshDefinitions();
      }
      toast.error(error?.response?.data?.message || "Could not open saved report");
    }
  }, [instanceUniqueId, normalizedReportKey, refreshDefinitions]);

  useEffect(() => {
    refreshDefinitions();
  }, [refreshDefinitions]);

  useEffect(() => {
    if (!open) return undefined;
    return subscribeReportDefinitionsInvalidation(
      instanceUniqueId,
      refreshDefinitions,
      normalizedReportKey,
    );
  }, [instanceUniqueId, normalizedReportKey, open, refreshDefinitions]);

  useEffect(() => {
    loadSelectedDefinition(selectedId);
  }, [loadSelectedDefinition, selectedId]);

  useEffect(() => {
    if (!open) {
      setSelectedId(null);
      setSelectedDefinition(null);
      setQuery("");
      setCreateTitle("");
      setCreateDesc("");
      setCreateVisibility("private");
      setEditTitle("");
      setEditDesc("");
      setEditVisibility("private");
      setDeletingIds({});
      setPinningIds({});
    }
  }, [open]);

  const sortedDefinitions = useMemo(() => {
    const items = Array.isArray(definitions) ? [...definitions] : [];
    items.sort((left, right) => {
      const leftPinned = isPinned(left) ? 1 : 0;
      const rightPinned = isPinned(right) ? 1 : 0;
      if (leftPinned !== rightPinned) return rightPinned - leftPinned;
      return String(left?.name || "").localeCompare(String(right?.name || ""), undefined, { sensitivity: "base" });
    });
    return items;
  }, [definitions]);

  const saveNew = async () => {
    const name = String(createTitle || "").trim();
    if (!name || creating) {
      if (!name) toast("Please enter a title.");
      return;
    }

    try {
      setCreating(true);
      const created = await createReportDefinition({
        reportKey: normalizedReportKey,
        name,
        description: String(createDesc || "").trim() || undefined,
        visibility: "private",
        config_json: makeConfig({
          instanceUniqueId,
          reportKey: normalizedReportKey,
          ui: currentState,
          pinned: false,
        }),
      }, instanceUniqueId);
      toast.success("Report saved");
      setSelectedId(created?.id || null);
      setCreateTitle("");
      setCreateDesc("");
      await refreshDefinitions();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "Could not save report");
    } finally {
      setCreating(false);
    }
  };

  const updateExisting = async () => {
    if (!selectedDefinition?.id || !canEditDefinition(selectedDefinition) || updating) return;
    const name = String(editTitle || "").trim();
    if (!name) {
      toast("Please enter a title.");
      return;
    }

    try {
      setUpdating(true);
      const updated = await updateReportDefinition(selectedDefinition.id, {
        reportKey: normalizedReportKey,
        name,
        description: String(editDesc || "").trim() || undefined,
        visibility: "private",
        config_json: makeConfig({
          instanceUniqueId,
          reportKey: normalizedReportKey,
          ui: currentState,
          pinned: isPinned(selectedDefinition),
        }),
      }, instanceUniqueId);
      toast.success("Report updated");
      setSelectedDefinition(updated || null);
      await refreshDefinitions();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "Could not update report");
    } finally {
      setUpdating(false);
    }
  };

  const deleteSelected = async (definition = selectedDefinition) => {
    if (!definition?.id || !canEditDefinition(definition) || deletingIds[definition.id]) return;
    const ok = window.confirm(`Delete "${definition.name || "Untitled"}"? This cannot be undone.`);
    if (!ok) return;

    try {
      setDeletingIds((previous) => ({ ...previous, [definition.id]: true }));
      await deleteReportDefinition(definition.id, instanceUniqueId, { reportKey: normalizedReportKey });
      if (String(selectedId) === String(definition.id)) {
        setSelectedId(null);
        setSelectedDefinition(null);
      }
      toast.success("Deleted");
      await refreshDefinitions();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "Delete failed");
    } finally {
      setDeletingIds((previous) => {
        const next = { ...previous };
        delete next[definition.id];
        return next;
      });
    }
  };

  const togglePin = async (definition) => {
    if (!definition?.id || !canEditDefinition(definition) || pinningIds[definition.id]) return;
    const requestedPinned = !isPinned(definition);
    try {
      setPinningIds((previous) => ({ ...previous, [definition.id]: true }));
      const updated = await pinReportDefinition(definition.id, {
        instanceUniqueId,
        reportKey: normalizedReportKey,
        pinned: requestedPinned,
      });
      if (isPinned(updated) !== requestedPinned) {
        throw new Error("Saved report pin state did not persist.");
      }
      if (String(selectedId) === String(definition.id)) {
        setSelectedDefinition(updated || definition);
      }
      toast.success(requestedPinned ? "Pinned" : "Unpinned");
      await refreshDefinitions();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || error?.message || "Could not update pin");
    } finally {
      setPinningIds((previous) => {
        const next = { ...previous };
        delete next[definition.id];
        return next;
      });
    }
  };

  const applySelected = async () => {
    if (!selectedDefinition) return;
    const savedState = readConfig(selectedDefinition);
    if (!savedState) {
      toast.error("This saved report has no saved configuration.");
      return;
    }
    try {
      setApplying(true);
      await onApplyReport?.(savedState, selectedDefinition);
      toast.success("Saved report applied");
      setOpen(false);
    } catch (error) {
      if (error?.response?.status === 404) {
        setSelectedId(null);
        setSelectedDefinition(null);
        await refreshDefinitions();
      }
      toast.error(
        error?.response?.data?.message
        || error?.message
        || "Could not apply saved report",
      );
    } finally {
      setApplying(false);
    }
  };

  return (
    <>
      <Tooltip title={title}>
        <IconButton
          size="small"
          aria-label={title}
          onClick={() => setOpen(true)}
          sx={{ color: "text.secondary" }}
        >
          <HistoryIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { width: 960 } }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "340px 1fr", height: "100%" }}>
          <Box sx={{ borderRight: "1px solid #eee", p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="h6">Reports</Typography>
              <IconButton onClick={() => setOpen(false)} size="large"><CloseIcon /></IconButton>
            </Box>

            <TextField
              size="small"
              fullWidth
              placeholder="Search saved reports..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              InputProps={{ endAdornment: <SearchIcon fontSize="small" /> }}
              sx={{ mb: 1 }}
            />

            <Divider sx={{ mb: 1 }} />

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}><CircularProgress size={24} /></Box>
            ) : sortedDefinitions.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No saved reports yet.</Typography>
            ) : (
              <List dense sx={{ overflow: "auto", maxHeight: "calc(100vh - 210px)" }}>
                {sortedDefinitions.map((definition) => {
                  const pinned = isPinned(definition);
                  const canEdit = canEditDefinition(definition);
                  const deleting = Boolean(deletingIds[definition.id]);
                  const pinning = Boolean(pinningIds[definition.id]);

                  return (
                    <ListItemButton
                      key={definition.id}
                      selected={String(selectedId) === String(definition.id)}
                      onClick={() => setSelectedId(definition.id)}
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <ListItemText
                        primary={<span style={{ fontWeight: 700 }}>{definition.name || "Untitled"}</span>}
                        secondary={definition.description || null}
                      />
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {canEdit ? (
                          <IconButton
                            edge="end"
                            size="small"
                            title={pinned ? "Unpin" : "Pin"}
                            disabled={pinning || applying}
                            onClick={(event) => {
                              event.stopPropagation();
                              togglePin(definition);
                            }}
                          >
                            {pinning ? <CircularProgress size={16} /> : pinned ? <StarIcon color="warning" /> : <StarBorderIcon />}
                          </IconButton>
                        ) : null}
                        {canEdit ? (
                          <IconButton
                            edge="end"
                            size="small"
                            title="Delete"
                            disabled={deleting || applying}
                            onClick={(event) => {
                              event.stopPropagation();
                              deleteSelected(definition);
                            }}
                          >
                            {deleting ? <CircularProgress size={16} /> : <DeleteForeverIcon color="error" />}
                          </IconButton>
                        ) : null}
                      </Box>
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Box>

          <Box sx={{ p: 2, display: "grid", gridTemplateRows: "auto 1fr auto", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "space-between" }}>
              <Typography variant="h6">Saved reports</Typography>
              <IconButton onClick={() => setOpen(false)} size="large"><CloseIcon /></IconButton>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 2 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    Create New
                  </Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                    <TextField
                      label="Title (required)"
                      value={createTitle}
                      onChange={(event) => setCreateTitle(event.target.value)}
                      required
                    />
                    <TextField
                      select
                      label="Visibility"
                      value={createVisibility}
                      onChange={(event) => setCreateVisibility(event.target.value)}
                    >
                      <MenuItem value="private">Private</MenuItem>
                    </TextField>
                    <TextField
                      label="Description (optional)"
                      value={createDesc}
                      onChange={(event) => setCreateDesc(event.target.value)}
                      placeholder="Optional notes"
                      multiline
                      minRows={2}
                      sx={{ gridColumn: "1 / span 2" }}
                    />
                    <Box sx={{ gridColumn: "1 / span 2", display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={saveNew}
                        disabled={creating || updating || applying || !String(createTitle || "").trim()}
                        sx={{ textTransform: "unset" }}
                      >
                        {creating ? "Saving..." : "Save current report"}
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {selectedDefinition ? (
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Edit Selected
                      </Typography>
                      {canEditDefinition(selectedDefinition) ? (
                        <IconButton
                          size="small"
                          title={isPinned(selectedDefinition) ? "Unpin" : "Pin"}
                          disabled={Boolean(pinningIds[selectedDefinition.id]) || creating || updating || applying}
                          onClick={() => togglePin(selectedDefinition)}
                        >
                          {pinningIds[selectedDefinition.id]
                            ? <CircularProgress size={16} />
                            : isPinned(selectedDefinition)
                              ? <StarIcon color="warning" />
                              : <StarBorderIcon />}
                        </IconButton>
                      ) : null}
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                      <TextField
                        label="Title"
                        value={editTitle}
                        onChange={(event) => setEditTitle(event.target.value)}
                        disabled={!canEditDefinition(selectedDefinition)}
                      />
                      <TextField
                        select
                        label="Visibility"
                        value={editVisibility}
                        onChange={(event) => setEditVisibility(event.target.value)}
                        disabled={!canEditDefinition(selectedDefinition)}
                      >
                        <MenuItem value="private">Private</MenuItem>
                      </TextField>
                      <TextField
                        label="Description"
                        value={editDesc}
                        onChange={(event) => setEditDesc(event.target.value)}
                        multiline
                        minRows={2}
                        disabled={!canEditDefinition(selectedDefinition)}
                        sx={{ gridColumn: "1 / span 2" }}
                      />
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        startIcon={<UpdateIcon />}
                        onClick={updateExisting}
                        disabled={!canEditDefinition(selectedDefinition) || creating || updating || applying || !String(editTitle || "").trim()}
                        sx={{ textTransform: "unset" }}
                      >
                        {updating ? "Updating..." : "Update saved report"}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={applySelected}
                        disabled={creating || updating || applying}
                        sx={{ textTransform: "unset" }}
                      >
                        {applying ? "Applying..." : "Apply to report"}
                      </Button>
                      {canEditDefinition(selectedDefinition) ? (
                        <Button
                          color="error"
                          variant="outlined"
                          startIcon={<DeleteForeverIcon />}
                          onClick={() => deleteSelected(selectedDefinition)}
                          disabled={Boolean(deletingIds[selectedDefinition.id]) || creating || updating || applying}
                          sx={{ textTransform: "unset" }}
                        >
                          Delete
                        </Button>
                      ) : null}
                    </Stack>
                    {!canEditDefinition(selectedDefinition) ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                        You can apply this saved report, but only the owner or an authorized editor can change it.
                      </Typography>
                    ) : null}
                  </CardContent>
                </Card>
              ) : (
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Select a saved report
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Choose a saved report from the list to apply it or update it with the current report state.
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
